import { AnyAction } from 'redux';
import {
  GlobalActions,
  VersicherungsnehmerCommonInsuranceData,
} from '@ps20-frontend/shop-integration-redux';
import {
  determineAssekuranztarifFromTarifbereich,
  IAnimalValueLabelElement,
  TierartEnum,
  TkvV2Vorbelegungdaten,
  ValueLabelElement
} from '@ps20-frontend/frontend-models';
import {
  AnimalIllness,
  AnimalIllnesses,
  BreedSelectionType,
  DiagnoseVorvertrag,
  KennzeichnungsArt,
  SpartenType,
  Tarifbereich,
  Tierart,
  Tiergeschlecht,
  Tierhaltung
} from '@ps20/bff-models';
import cloneDeep from 'lodash/cloneDeep';
import { convertDateFormatFromShortIsoToLocalState } from '@ps20-frontend/utils';
import { refreshBreedLabel } from '@ps20-frontend/migrations';
import { SelectedIllnessesChangedPayload, TkvActions } from '../tkvActions';
import { IAppState } from '../appState';

export enum DogBreedType {
  SELECTED = 'SELECTED',
  MIXED = 'MIXED',
  UNKNOWN = 'UNKNOWN',
  MIXED_OR_UNKNOWN = 'MIXED_OR_UNKNOWN',
}

export enum DogSize {
  SMALL = 'SMALL',
  LARGE = 'LARGE',
}

export interface Versicherungsnehmer extends VersicherungsnehmerCommonInsuranceData {
  animalType: TierartEnum;
  animalGender: Tiergeschlecht;
  tarifbereich?: Tarifbereich;
  animalName: string;
  animalBirthDate: string;
  dogBreeds: IAnimalValueLabelElement[];
  horseBreeds: ValueLabelElement[];
  catBreeds: ValueLabelElement[];
  breed?: ValueLabelElement;
  dogBreedType?: DogBreedType;
  dogMotherBreed?: ValueLabelElement;
  dogMotherBreedType?: DogBreedType;
  dogSize?: DogSize;
  catKeepingType?: Tierhaltung;
  animalIllnesses: AnimalIllnesses;
  hasIllnesses: boolean;
  selectedIllnesses?: AnimalIllness[];
  wantsCoverageUpgrade?: boolean;
  useExistingWechselbaustein?: boolean;
  previousDiagnoses?: DiagnoseVorvertrag[];
  kennzeichnungsArt?: KennzeichnungsArt;
  kennzeichen?: string;
  isAssistanceDog?: boolean;
}

export const versicherungsnehmerReducer = function (
  sectionState: Versicherungsnehmer = null,
  action: AnyAction,
  state: IAppState,
): Versicherungsnehmer {
  switch (action.type) {
    case GlobalActions.UPDATE_ASSEKURANZTARIF_DATA:
    case GlobalActions.UPDATE_ASSEKURANZTARIF_DATA_AFTER_VV_LOAD: {
      return {
        ...sectionState,
        isAssekuranztarif: action.payload,
      };
    }
    case TkvActions.ANIMAL_TYPE_PREFILLED: {
      return { ...sectionState, animalType: action.payload };
    }
    case TkvActions.ANIMAL_TYPE_CHANGED: {
      const animalType: TierartEnum = action.payload;
      const breed: ValueLabelElement = null;
      const dogBreedType: DogBreedType =
        animalType === TierartEnum.HUND ? DogBreedType.SELECTED : null;
      const dogMotherBreed: ValueLabelElement = null;
      const dogMotherBreedType: DogBreedType = null;
      const dogSize: DogSize = null;
      const catKeepingType: Tierhaltung = null;
      const hasIllnesses: boolean = null;
      const selectedIllnesses: AnimalIllness[] = [];
      return {
        ...sectionState,
        animalType,
        breed,
        dogBreedType,
        dogMotherBreed,
        dogMotherBreedType,
        dogSize,
        catKeepingType,
        hasIllnesses,
        selectedIllnesses,
      };
    }
    case TkvActions.ANIMAL_GENDER_CHANGED: {
      const animalGender: Tiergeschlecht = action.payload;
      return { ...sectionState, animalGender };
    }
    case TkvActions.ANIMAL_NAME_CHANGED: {
      const animalName: string = action.payload;
      return { ...sectionState, animalName };
    }
    case TkvActions.ANIMAL_BIRTH_DATE_CHANGED: {
      const animalBirthDate: string = action.payload;
      return { ...sectionState, animalBirthDate };
    }
    case GlobalActions.HUNDERASSENLISTE_RETRIEVED: {
      const dogBreeds: ValueLabelElement[] = action.payload;
      return refreshBreedLabelsFromCatalog({ ...sectionState, dogBreeds });
    }
    case GlobalActions.PFERDERASSEN_LISTE_RETRIEVED: {
      const horseBreeds: ValueLabelElement[] = action.payload;
      return refreshBreedLabelsFromCatalog({ ...sectionState, horseBreeds });
    }
    case GlobalActions.KATZENRASSEN_LISTE_RETRIEVED: {
      const catBreeds: ValueLabelElement[] = action.payload;
      return refreshBreedLabelsFromCatalog({ ...sectionState, catBreeds });
    }
    case GlobalActions.ANIMAL_ILLNESSES_RECEIVED: {
      const animalIllnesses: AnimalIllnesses = action.payload;
      return { ...sectionState, animalIllnesses };
    }
    case TkvActions.BREED_CHANGED: {
      const breed: ValueLabelElement = action.payload;
      return { ...sectionState, breed };
    }
    case TkvActions.DOG_BREED_TYPE_CHANGED: {
      const dogBreedType: DogBreedType = action.payload;
      const breed: ValueLabelElement = null;
      const dogMotherBreed: ValueLabelElement = null;
      const dogMotherBreedType: DogBreedType =
        dogBreedType === DogBreedType.MIXED ? DogBreedType.SELECTED : null;
      const dogSize: DogSize = null;
      return {
        ...sectionState,
        breed,
        dogBreedType,
        dogMotherBreed,
        dogMotherBreedType,
        dogSize,
      };
    }
    case TkvActions.DOG_MOTHER_BREED_CHANGED: {
      const dogMotherBreed: ValueLabelElement = action.payload;
      return { ...sectionState, dogMotherBreed };
    }
    case TkvActions.DOG_MOTHER_BREED_TYPE_CHANGED: {
      const dogMotherBreedType: DogBreedType = action.payload;
      const dogMotherBreed: ValueLabelElement = null;
      const dogSize: DogSize = null;
      return { ...sectionState, dogMotherBreedType, dogMotherBreed, dogSize };
    }
    case TkvActions.DOG_SIZE_CHANGED: {
      const dogSize: DogSize = action.payload;
      return { ...sectionState, dogSize };
    }
    case TkvActions.CAT_KEEPING_TYPE_CHANGED: {
      const catKeepingType: Tierhaltung = action.payload;
      return { ...sectionState, catKeepingType };
    }
    case TkvActions.WANTS_COVERAGE_UPGRADE_CHANGED: {
      const wantsCoverageUpgrade: boolean = action.payload;
      const useExistingWechselbaustein: boolean = null;
      const hasIllnesses: boolean = null;
      const selectedIllnesses: AnimalIllness[] = null;
      return { ...sectionState, wantsCoverageUpgrade, useExistingWechselbaustein, hasIllnesses, selectedIllnesses };
    }
    case TkvActions.USE_EXISTING_WECHSELBAUSTEIN_CHANGED: {
      const useExistingWechselbaustein: boolean = action.payload;
      const hasIllnesses: boolean = null;
      const selectedIllnesses: AnimalIllness[] = null;
      return { ...sectionState, useExistingWechselbaustein, hasIllnesses, selectedIllnesses };
    }
    case TkvActions.HAS_ILLNESSES_CHANGED: {
      const hasIllnesses: boolean = action.payload;
      const selectedIllnesses: AnimalIllness[] = null;
      return { ...sectionState, hasIllnesses, selectedIllnesses };
    }
    case TkvActions.SELECTED_ILLNESSES_CHANGED: {
      const { name, value }: SelectedIllnessesChangedPayload = action.payload;
      let selectedIllnesses: AnimalIllness[] = cloneDeep(sectionState.selectedIllnesses) ?? [];
      const availableIllnesses =
        sectionState.animalType === TierartEnum.PFERD
          ? sectionState.animalIllnesses.horseIllnesses
          : sectionState.animalIllnesses.dogCatIllnesses;
      if (value) {
        selectedIllnesses.push(
          availableIllnesses.find(availableIllness => availableIllness.name === name),
        );
      } else {
        selectedIllnesses = selectedIllnesses.filter(
          selectedIllness => selectedIllness.name !== name,
        );
      }
      return { ...sectionState, selectedIllnesses };
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED:
    case TkvActions.VORBELEGUNG_FOR_TKV_ERSATZ_REQUESTED: {
      const tkvVorbelegungdaten: TkvV2Vorbelegungdaten = action.payload;
      if (
        state.produkt.spartenType === SpartenType.TIERKRANKEN_V2 &&
        tkvVorbelegungdaten.angebotsId === state.angebotsId
      ) {
        const tierart: TierartEnum = convertFromBffTierart(tkvVorbelegungdaten.tierart);
        const breedSelectionType = tkvVorbelegungdaten.breedSelectionType;

        const breedVorbelegung = determineBreedVorbelegung(
          tierart,
          breedSelectionType,
          tkvVorbelegungdaten.tierrasse,
          sectionState
        );

        return {
          ...sectionState,
          animalName: tkvVorbelegungdaten.tiername,
          catKeepingType: tkvVorbelegungdaten.tierhaltung,
          animalType: tierart,
          animalGender: tkvVorbelegungdaten.tiergeschlecht ?? Tiergeschlecht.MAENNLICH,
          animalBirthDate: convertDateFormatFromShortIsoToLocalState(
            tkvVorbelegungdaten.geburtsdatumTier,
          ),
          previousDiagnoses: tkvVorbelegungdaten.diagnose,
          kennzeichnungsArt: tkvVorbelegungdaten.kennzeichnungsArt,
          kennzeichen: tkvVorbelegungdaten.kennzeichen,
          isAssistanceDog: tkvVorbelegungdaten.hasAssistenzhund,
          ...breedVorbelegung,
        };
      }
      return sectionState;
    }
    case GlobalActions.TARIFBEREICH_CHANGED: {
      return {
        ...sectionState,
        tarifbereich: action.payload,
        isAssekuranztarif: determineAssekuranztarifFromTarifbereich(action.payload),
      };
    }
    default:
      return sectionState;
  }
};

interface BreedVorbelegungResult {
  breed: ValueLabelElement;
  dogBreedType?: DogBreedType;
  dogMotherBreed?: ValueLabelElement;
  dogMotherBreedType?: DogBreedType;
  dogSize?: DogSize;
}

function determineBreedVorbelegung(
  tierart: TierartEnum,
  breedSelectionType: BreedSelectionType,
  tierrasse: string,
  sectionState: Versicherungsnehmer
): BreedVorbelegungResult {
  const rasse = getRasseLabel(tierart, sectionState, tierrasse);

  if (tierart !== TierartEnum.HUND) {
    return {
      breed: { value: tierrasse, label: rasse },
    };
  }

  switch (breedSelectionType) {
    case BreedSelectionType.MIX: {
      // For MIX, tierrasse contains the motherBreed code (e.g., D425 for Maltipoo Mix)
      const breedEntry = sectionState.dogBreeds?.find(
        breed => breed.motherBreed?.value === tierrasse
      );
      const motherBreed = breedEntry?.motherBreed;

      return {
        breed: null,
        dogBreedType: DogBreedType.MIXED,
        dogMotherBreedType: DogBreedType.SELECTED,
        dogMotherBreed: motherBreed ? { value: motherBreed.value, label: motherBreed.label } : null,
        dogSize: null,
      };
    }
    case BreedSelectionType.UNKNOWN_HEIGHT_SMALL: {
      return {
        breed: null,
        dogBreedType: DogBreedType.UNKNOWN,
        dogMotherBreed: null,
        dogMotherBreedType: null,
        dogSize: DogSize.SMALL,
      };
    }
    case BreedSelectionType.UNKNOWN_HEIGHT_LARGE: {
      return {
        breed: null,
        dogBreedType: DogBreedType.UNKNOWN,
        dogMotherBreed: null,
        dogMotherBreedType: null,
        dogSize: DogSize.LARGE,
      };
    }
    case BreedSelectionType.KNOWN_BREED:
    default: {
      return {
        breed: { value: tierrasse, label: rasse },
        dogBreedType: DogBreedType.SELECTED,
        dogMotherBreed: null,
        dogMotherBreedType: null,
        dogSize: null,
      };
    }
  }
}

export function convertFromBffTierart(tierart: Tierart): TierartEnum {
  switch (tierart) {
    case Tierart.VRHUND:
      return TierartEnum.HUND;
    case Tierart.VRKATZE:
      return TierartEnum.KATZE;
    case Tierart.VRPFERD:
      return TierartEnum.PFERD;
    default:
      return null;
  }
}

export function getRasseLabel(
  tierart: TierartEnum,
  versicherungsnehmer: Versicherungsnehmer,
  rasseWert: string,
): string {
  switch (tierart) {
    case TierartEnum.KATZE:
      return versicherungsnehmer.catBreeds?.find(rassen => rassen.value === rasseWert)?.label;
    case TierartEnum.PFERD:
      return versicherungsnehmer.horseBreeds?.find(rassen => rassen.value === rasseWert)?.label;
    case TierartEnum.HUND:
      return versicherungsnehmer.dogBreeds?.find(rassen => rassen.value === rasseWert)?.label;
    default:
      return null;
  }
}

function getDogMotherBreedLabel(
  versicherungsnehmer: Versicherungsnehmer,
  motherBreedWert: string,
): string {
  return versicherungsnehmer.dogBreeds?.find(
    breed => breed.motherBreed?.value === motherBreedWert,
  )?.motherBreed?.label;
}

function refreshBreedWithMotherBreed(
  versicherungsnehmer: Versicherungsnehmer,
  breed: IAnimalValueLabelElement,
  freshLabel: string,
): IAnimalValueLabelElement {
  const refreshed = refreshBreedLabel(breed, freshLabel);
  if (!refreshed?.motherBreed) {
    return refreshed;
  }
  return {
    ...refreshed,
    motherBreed: refreshBreedLabel(
      refreshed.motherBreed,
      getDogMotherBreedLabel(versicherungsnehmer, refreshed.motherBreed.value),
    ),
  };
}

export function refreshBreedLabelsFromCatalog(
  versicherungsnehmer: Versicherungsnehmer,
): Versicherungsnehmer {
  if (!versicherungsnehmer) {
    return versicherungsnehmer;
  }
  const tierart = versicherungsnehmer.animalType;
  const breed = refreshBreedWithMotherBreed(
    versicherungsnehmer,
    versicherungsnehmer.breed as IAnimalValueLabelElement,
    versicherungsnehmer.breed
      ? getRasseLabel(tierart, versicherungsnehmer, versicherungsnehmer.breed.value)
      : null,
  );
  const dogMotherBreed = refreshBreedLabel(
    versicherungsnehmer.dogMotherBreed,
    versicherungsnehmer.dogMotherBreed
      ? getDogMotherBreedLabel(versicherungsnehmer, versicherungsnehmer.dogMotherBreed.value)
      : null,
  );
  return { ...versicherungsnehmer, breed, dogMotherBreed };
}
